import { ThemeProvider } from "@/components/theme-provider";
import { signOut } from "@/lib/queries/session";
import { trpcClient } from "@/lib/trpc";
import { Button, Spinner } from "@repo/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/logout")({
  component: LogoutPage,
});

export function LogoutPage() {
  const queryClient = useQueryClient();
  const { mutateAsync: revokeSessionsFn } = useMutation({
    mutationFn: () => trpcClient.user.revokeAllSessions.mutate(),
  });

  useEffect(() => {
    let isMounted = true;

    const performLogout = async () => {
      try {
        // Best-effort: revoke all sessions on the backend.
        // May fail if the session is already expired — that's fine,
        // there's nothing to revoke.
        try {
          await revokeSessionsFn();
        } catch {
          // Session already invalid — continue with local cleanup
        }

        if (!isMounted) return;

        // Always run: clears server session cookie, invalidates query
        // cache, and resets analytics (Datadog, PostHog)
        await signOut(queryClient, { redirect: false });

        if (!isMounted) return;

        // Force hard redirect to login, preserving any returnTo destination
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get("returnTo");
        const loginUrl = returnTo
          ? `/login?returnTo=${encodeURIComponent(returnTo)}`
          : "/login";
        window.location.href = loginUrl;
      } catch {
        // signOut itself failed — still redirect
        if (isMounted) {
          const params = new URLSearchParams(window.location.search);
          const returnTo = params.get("returnTo");
          window.location.href = returnTo
            ? `/login?returnTo=${encodeURIComponent(returnTo)}`
            : "/login";
        }
      } finally {
        // Cleanup imported theme
        localStorage.removeItem("imported-theme-url");
        localStorage.removeItem("imported-theme-css");
      }
    };

    performLogout();

    return () => {
      isMounted = false;
    };
  }, [revokeSessionsFn, queryClient]);

  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <div className="relative flex min-h-svh flex-col items-center justify-center p-6 md:p-10 overflow-hidden">
        <div className="flex min-h-screen flex-col items-center justify-center">
          <Button disabled size="lg" variant="secondary">
            <Spinner data-icon="inline-start" />
            Logging out...
          </Button>
        </div>
      </div>
    </ThemeProvider>
  );
}
